/**
 * The unsaved draft of one inline text-edit session.
 *
 * While a session is open its typing is previewed - the draft is spliced into
 * the RENDERED document so the paginator re-measures it and a new page appears
 * as the text grows - but nothing is stored until the session commits. The
 * draft therefore keeps two things apart: the span of the STORED body the
 * session will replace on commit (`base`, captured from the stored document,
 * whose original text guards the commit's splice), and the draft markdown for
 * that whole span. The live editor only ever shows one page's fragment of it -
 * the `view` sub-slice - so when the draft repaginates and the caret's text
 * moves to another fragment, the editor's content is folded back into the
 * span and the span is widened to cover the new fragment. The commit then
 * replaces the whole original span with the whole draft, however many pages
 * the session crossed.
 *
 * Offsets in the "draft body" are into the stored body with the span replaced
 * by the draft: the coordinates the rendered fragments stamp while the draft
 * is shown.
 */

import type { SourceTextRange } from "./renderedTextEdit";
import { BLANK_LINE } from "./textEditModel";

/** A `[start, end)` window of text offsets. */
export interface TextWindow {
  start: number;
  end: number;
}

/** The draft one inline text-edit session holds until it commits. */
export interface TextEditDraft {
  /** The stored, dedented body the span indexes ("" for a whole-body session). */
  body: string;
  /** The stored span the commit replaces, or null for the whole body. */
  base: SourceTextRange | null;
  /** The draft markdown for the whole span. */
  markdown: string;
  /** The sub-slice of `markdown` the live editor shows. */
  view: TextWindow;
  /** Whether an edit has been folded into the draft. */
  changed: boolean;
}

/**
 * Opens a session's draft with the editor showing the whole span.
 * @param body - The stored, dedented body ("" for a whole-body session)
 * @param base - The stored span the session edits, or null for the whole body
 * @param markdown - The markdown seeding the session
 * @returns The fresh, unchanged draft
 */
export function openTextEditDraft(
  body: string,
  base: SourceTextRange | null,
  markdown: string,
): TextEditDraft {
  return {
    body,
    base,
    markdown,
    view: { start: 0, end: markdown.length },
    changed: false,
  };
}

/**
 * The markdown the live editor was seeded with.
 * @param draft - The session draft
 * @returns The view's slice of the draft
 */
export function viewMarkdown(draft: TextEditDraft): string {
  return draft.markdown.slice(draft.view.start, draft.view.end);
}

/** A view holding at most one blank line, which seeds a lone empty paragraph. */
const LONE_BLANK_LINE = new RegExp(`^[ \\t\\n]*${BLANK_LINE}?[ \\t\\n]*$`);

/**
 * The markdown the live editor serializes to while untouched since its seed:
 * its view, or nothing for a view of one blank line - that seeds the editor's
 * emptied state, a lone empty paragraph, which serializes to nothing. A view
 * of several blank lines is kept: those are lines of the body.
 * @param draft - The session draft
 * @returns The editor's markdown before any edit since its seed
 */
export function seededEditorMarkdown(draft: TextEditDraft): string {
  const seeded = viewMarkdown(draft);
  return LONE_BLANK_LINE.test(seeded) ? "" : seeded;
}

/**
 * The span markdown a session commits: the folded draft, or nothing when a
 * whole-body session left only blank lines - an emptied block stores an empty
 * body, as editorDocToMarkdown does, while the preview kept every line.
 * @param draft - The session draft
 * @param editorMarkdown - The live editor's slice markdown
 * @returns The markdown to splice over the span
 */
export function committedSpanMarkdown(
  draft: TextEditDraft,
  editorMarkdown: string,
): string {
  const markdown = foldEditorMarkdown(draft, editorMarkdown);
  return !draft.base && markdown.trim() === "" ? "" : markdown;
}

/**
 * The markdown an editor previews as. An emptied editor still shows one empty
 * paragraph holding the caret, but serializes to nothing; previewed as nothing,
 * its fragment (and possibly its page) would vanish under the caret and the
 * next keystroke would land at the end of the previous page. A stored blank
 * line keeps that line in the preview, matching what the editor shows.
 * @param editorMarkdown - The live editor's serialized markdown
 * @returns The markdown to preview and hand over with
 */
export function previewMarkdown(editorMarkdown: string): string {
  return editorMarkdown === "" ? BLANK_LINE : editorMarkdown;
}

/**
 * The span's draft with the editor's current content folded over its view.
 * @param draft - The session draft
 * @param editorMarkdown - The live editor's serialized markdown
 * @returns The whole span's draft markdown
 */
export function foldEditorMarkdown(
  draft: TextEditDraft,
  editorMarkdown: string,
): string {
  return (
    draft.markdown.slice(0, draft.view.start) +
    editorMarkdown +
    draft.markdown.slice(draft.view.end)
  );
}

/**
 * Where the live editor's content sits in the draft body.
 * @param draft - The session draft
 * @param editorMarkdown - The live editor's serialized markdown
 * @returns The editor's window, in draft-body offsets
 */
export function editorBodyWindow(
  draft: TextEditDraft,
  editorMarkdown: string,
): TextWindow {
  const start = (draft.base?.start ?? 0) + draft.view.start;
  return { start, end: start + editorMarkdown.length };
}

/** Whitespace a rendered fragment's window leaves off its last unit's end. */
const TRAILING_WHITESPACE = /^[ \t\n]*$/;

/**
 * Whether a rendered fragment shows exactly the live editor's text: the same
 * start, and an end short of the editor's only by trailing whitespace, which a
 * unit's source range never covers.
 * @param fragment - The rendered fragment's window, in draft-body offsets
 * @param draft - The session draft
 * @param editorMarkdown - The live editor's serialized markdown
 * @returns True when the fragment and the editor hold the same units
 */
export function windowShowsEditor(
  fragment: TextWindow,
  draft: TextEditDraft,
  editorMarkdown: string,
): boolean {
  const view = editorBodyWindow(draft, editorMarkdown);
  return (
    fragment.start === view.start &&
    fragment.end <= view.end &&
    TRAILING_WHITESPACE.test(editorMarkdown.slice(fragment.end - view.start))
  );
}

/**
 * The length of the draft body: the stored body with the span replaced.
 * @param draft - The session draft
 * @param editorMarkdown - The live editor's serialized markdown
 * @returns The draft body's length
 */
export function draftBodyLength(
  draft: TextEditDraft,
  editorMarkdown: string,
): number {
  const folded = foldEditorMarkdown(draft, editorMarkdown).length;
  if (!draft.base) {
    return folded;
  }
  return draft.body.length - (draft.base.end - draft.base.start) + folded;
}

/**
 * Hands the draft's view over to another rendered fragment: folds the
 * editor's content into the span, widens the span (in both the stored and
 * draft coordinates) to cover the fragment, and points the view at it.
 * @param draft - The session draft
 * @param editorMarkdown - The live editor's serialized markdown
 * @param fragment - The new fragment's window, in draft-body offsets
 * @param changed - Whether the draft holds edits once the editor is folded in
 * @returns The rebased draft
 */
export function rebaseTextEditDraft(
  draft: TextEditDraft,
  editorMarkdown: string,
  fragment: TextWindow,
  changed: boolean,
): TextEditDraft {
  const markdown = foldEditorMarkdown(draft, editorMarkdown);
  const length = draftBodyLength(draft, editorMarkdown);
  const end = Math.min(Math.max(fragment.end, 0), length);
  const start = Math.min(Math.max(fragment.start, 0), end);
  if (!draft.base) {
    return { ...draft, markdown, view: { start, end }, changed };
  }
  const { base, body } = draft;
  const draftBody = body.slice(0, base.start) + markdown + body.slice(base.end);
  const spanEnd = base.start + markdown.length;
  const spanStart = Math.min(base.start, start);
  const nextSpanEnd = Math.max(spanEnd, end);
  const storedEnd = base.end + (nextSpanEnd - spanEnd);
  return {
    body,
    base: {
      start: spanStart,
      end: storedEnd,
      text: body.slice(spanStart, storedEnd),
    },
    markdown: draftBody.slice(spanStart, nextSpanEnd),
    view: { start: start - spanStart, end: end - spanStart },
    changed,
  };
}
